import { useEffect, useState } from "react";
import { Loader2, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useApiBase } from "../context/ApiBaseContext";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import VisibilityPicker from "./VisibilityPicker";

/**
 * Iter-131b — Add an external-document link (Google Sheets, Numbers,
 * Excel Online, etc.) to the project's change-documents collection.
 *
 * Lightweight integration philosophy — we don't try to embed or sync
 * the spreadsheet, just keep a calm reference pointer alongside the
 * uploaded documents so the crew can find it quickly.
 */
const CATEGORY_OPTIONS = [
    { value: "link", label: "Link (generic)" },
    { value: "schedule", label: "Schedule" },
    { value: "call_sheet", label: "Call sheet" },
    { value: "budget", label: "Budget" },
    { value: "reference", label: "Reference" },
    { value: "research", label: "Research" },
    { value: "location", label: "Location" },
    { value: "continuity", label: "Continuity" },
    { value: "email", label: "Email" },
];

export default function AddExternalLinkDialog({
    open,
    onOpenChange,
    projectId,
    onCreated,
    // Iter-156E — parity with AddSupervisorNoteDialog +
    // ChangeDocumentUploadDialog. Pre-fills scene/character link so
    // the new external link auto-attaches to the contextual page.
    presetSceneId = null,
    presetCharacterId = null,
}) {
    const apiBase = useApiBase();
    const [url, setUrl] = useState("");
    const [label, setLabel] = useState("");
    const [kind, setKind] = useState("link");
    // Iter-156E — visibility selector parity. Defaults to "everyone".
    const [visibility, setVisibility] = useState("everyone");
    const [submitting, setSubmitting] = useState(false);

    const reset = () => {
        setUrl("");
        setLabel("");
        setKind("link");
        setVisibility("everyone");
    };

    // Iter-156E — reset visibility every time the dialog reopens so
    // a previous tighter scope doesn't surprise the next user.
    useEffect(() => {
        if (open) {
            setVisibility("everyone");
        }
    }, [open]);

    const submit = async (e) => {
        e?.preventDefault?.();
        const trimmed = url.trim();
        if (!trimmed) {
            toast.error("Please paste the link URL.");
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await api.post(
                `${apiBase}/${projectId}/change-documents/link`,
                {
                    link_url: trimmed,
                    label: label.trim() || null,
                    kind,
                    visibility,
                    scene_id: presetSceneId || null,
                    character_id: presetCharacterId || null,
                },
            );
            toast.success("External link added");
            reset();
            onOpenChange(false);
            if (onCreated) onCreated(data?.document);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog
            open={open}
            onOpenChange={(v) => {
                if (!submitting) onOpenChange(v);
            }}
        >
            <DialogContent
                data-testid="add-external-link-dialog"
                className="max-w-md bg-card border-white/10"
            >
                <DialogHeader className="pr-8">
                    <DialogTitle className="font-display text-xl font-bold text-white flex items-center gap-2">
                        <LinkIcon size={16} />
                        Add external link
                    </DialogTitle>
                    <DialogDescription className="text-neutral-400">
                        Paste a Google Sheets, Excel Online, Numbers, or any
                        other URL. OSF keeps the reference; the file itself
                        opens in your browser.
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-3 mt-2">
                    <div>
                        <Label className="text-[11px] uppercase tracking-[0.2em] text-neutral-400">
                            URL
                        </Label>
                        <Input
                            type="url"
                            inputMode="url"
                            data-testid="add-external-link-url"
                            value={url}
                            onChange={(e) => setUrl(e.target.value)}
                            placeholder="https://docs.google.com/…"
                            autoFocus
                            required
                            className="mt-1"
                        />
                    </div>
                    <div>
                        <Label className="text-[11px] uppercase tracking-[0.2em] text-neutral-400">
                            Label (optional)
                        </Label>
                        <Input
                            type="text"
                            data-testid="add-external-link-label"
                            value={label}
                            onChange={(e) => setLabel(e.target.value)}
                            placeholder="e.g. Crowd cast sheet"
                            className="mt-1"
                        />
                    </div>
                    <div>
                        <Label className="text-[11px] uppercase tracking-[0.2em] text-neutral-400">
                            Category
                        </Label>
                        <select
                            data-testid="add-external-link-kind"
                            value={kind}
                            onChange={(e) => setKind(e.target.value)}
                            className="mt-1 w-full h-10 rounded-md border border-white/10 bg-secondary/50 px-3 text-sm text-white"
                        >
                            {CATEGORY_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>
                                    {o.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    {/* Iter-156E — visibility selector parity. */}
                    <VisibilityPicker
                        value={visibility}
                        onChange={setVisibility}
                        testidPrefix="add-external-link-visibility"
                    />
                    <div className="flex gap-2 pt-2">
                        <Button
                            type="button"
                            data-testid="add-external-link-cancel"
                            variant="secondary"
                            onClick={() => onOpenChange(false)}
                            disabled={submitting}
                            className="flex-1 min-tap"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            data-testid="add-external-link-submit"
                            disabled={submitting}
                            className="flex-1 min-tap font-semibold"
                        >
                            {submitting ? (
                                <Loader2 size={14} className="animate-spin" />
                            ) : (
                                "Add link"
                            )}
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
}
